import { createContext, type ReactNode, useContext, useRef, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { FullPageSpin } from '../FullPageSpin'

type FancyAppLoaderContextProps = {
  show: () => void
  hide: () => void
  forceShow: () => void
  forceHide: () => void
}

export const FancyAppLoaderContext = createContext<FancyAppLoaderContextProps>({
  show: () => null,
  hide: () => null,
  forceShow: () => null,
  forceHide: () => null,
})

type FancyAppLoaderProviderProps = {
  children: ReactNode
}

export const FancyAppLoaderProvider = ({ children }: FancyAppLoaderProviderProps) => {
  const [isLoaderVisible, setIsLoaderVisible] = useState(false)
  const [isLoaderForceVisible, setIsLoaderForceVisible] = useState(false)
  const [isFirstLoad, setIsFirstLoad] = useState(true)
  // Tracks whether the initial loader has actually been shown. The first load is
  // considered finished once that loader is hidden again — driven by the real
  // load lifecycle instead of a fixed timeout.
  const hasShownInitialLoader = useRef(false)

  return (
    <FancyAppLoaderContext.Provider
      value={{
        show: () => {
          if (isFirstLoad) {
            hasShownInitialLoader.current = true
          }
          setIsLoaderVisible(true)
        },
        hide: () => {
          if (isFirstLoad && hasShownInitialLoader.current) {
            setIsFirstLoad(false)
          }
          setIsLoaderVisible(false)
        },
        forceShow: () => setIsLoaderForceVisible(true),
        forceHide: () => setIsLoaderForceVisible(false),
      }}
    >
      {children}
      <div
        className={twMerge(
          'fixed left-0 right-0 top-0 bottom-0 bg-white transition-opacity',
          isLoaderForceVisible || (isFirstLoad && isLoaderVisible)
            ? 'opacity-100 pointer-events-auto'
            : 'opacity-0 pointer-events-none'
        )}
      >
        <FullPageSpin />
      </div>
    </FancyAppLoaderContext.Provider>
  )
}

export const useFancyAppLoader = () => useContext(FancyAppLoaderContext)
